import React, { useState, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, ScrollView, ActivityIndicator } from 'react-native';
import { useDispatch } from 'react-redux';
import LHOverlay from '../../../components/LHOverlay';
import Svg, { Path, Rect, Circle } from 'react-native-svg';
import FontFamily from '../../../constants/fonts';
import { formatUserName } from '../../../utils/emailTouserName';
import { createApiClient, apiClient, getLocalTimestamp, getTimezoneOffsetString } from '../../../api/apiClient';
import { setToastNotification, STANDUP_API_NAMES } from '../../../store/slices/standUpSlice';
import { handleStandUpErrorWithFriendlyMessage } from '../../../store/utils/standUpErrorHandler';
import { useAddNews } from '../hooks/useAddNews';

interface NewsItem {
  id: string;
  title: string;
  description: string;
  date: string;
  is_favourite: boolean;
  createdBy: string;
  is_truncated?: boolean;
  clientTimestamp?: string; // Local timestamp for backend conversion to UTC
  clientTimezone?: string; // Timezone offset for proper UTC calculation
}

interface NewsFeedOverlayProps {
  visible: boolean;
  onDismiss: () => void;
  onSave: (newsList: NewsItem[]) => void;
  userEmail: string;
  locationId: number | null;
  locationName: string;
  showDeleteButton?: boolean; // new optional prop to control delete visibility
}

const DeleteIcon = () => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path d="M14.12 10.47L12 12.59L9.87 10.47L8.46 11.88L10.59 14L8.47 16.12L9.88 17.53L12 15.41L14.12 17.53L15.53 16.12L13.41 14L15.53 11.88L14.12 10.47ZM15.5 4L14.5 3H9.5L8.5 4H5V6H19V4H15.5ZM6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19ZM8 9H16V19H8V9Z" fill="#034895" />
  </Svg>
);

const PlusIcon = () => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path d="M15.8334 10.8337H10.8334V15.8337H9.16675V10.8337H4.16675V9.16699H9.16675V4.16699H10.8334V9.16699H15.8334V10.8337Z" fill="#005CBC" />
  </Svg>
);

const InfoIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path d="M8 1.33334C4.32 1.33334 1.33334 4.32 1.33334 8C1.33334 11.68 4.32 14.6667 8 14.6667C11.68 14.6667 14.6667 11.68 14.6667 8C14.6667 4.32 11.68 1.33334 8 1.33334ZM8.66668 11.3333H7.33334V7.33334H8.66668V11.3333ZM8.66668 6H7.33334V4.66667H8.66668V6Z" fill="#C12C01" />
  </Svg>
);

const NewsFeedOverlay: React.FC<NewsFeedOverlayProps> = ({
  visible,
  onDismiss,
  onSave,
  userEmail,
  locationId,
  locationName,
  showDeleteButton = true, // default true for backwards compatibility
}) => {
  const dispatch = useDispatch();
  const [newsTitle, setNewsTitle] = useState('');
  const [newsDescription, setNewsDescription] = useState('');
  const [localNewsList, setLocalNewsList] = useState<NewsItem[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [titleError, setTitleError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  // Local inline error message shown inside the overlay so user doesn't miss it
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const scrollRef = useRef<ScrollView | null>(null);
  // Auto-hide duration for inline errors (in ms). Set to 2 minutes as requested
  const AUTO_HIDE_ERROR_MS = 2 * 60 * 1000;
  // Track which news item is being deleted
  const [deletingNewsId, setDeletingNewsId] = useState<string | null>(null);

  // Create a mock selectedLocation object from props for the hook
  const mockSelectedLocation = {
    id: locationId ?? undefined,
    displayText: locationName,
  };
  const { addNews } = useAddNews({ selectedLocation: mockSelectedLocation });

  // Clear any existing error when the overlay is opened so it doesn't persist across open/close
  useEffect(() => {
    if (visible) {
      setErrorMessage(null);
      setTitleError(false);
      setDescriptionError(false);
    }
  }, [visible]);

  // Auto-hide the inline error after AUTO_HIDE_ERROR_MS
  useEffect(() => {
    if (!errorMessage) return;
    const id = setTimeout(() => setErrorMessage(null), AUTO_HIDE_ERROR_MS);
    return () => clearTimeout(id);
  }, [errorMessage]);

  const handleCancel = () => {
    setNewsTitle('');
    setNewsDescription('');
    setLocalNewsList([]);
    // Clear any inline error when closing
    setErrorMessage(null);
    onDismiss();
  };

  const handleDeleteNews = async (newsId: string) => {
    // Check if this is a temporary news item (not yet saved to backend)
    // Temporary news IDs start with 'news-' prefix
    const isTemporaryNews = newsId.startsWith('news-');

    if (isTemporaryNews) {
      // For temporary news, just remove from local state without API call
      setLocalNewsList(prev => prev.filter(news => news.id !== newsId));
      return;
    }

    // For saved news, call DELETE API first
    setDeletingNewsId(newsId);
    try {
      const api = createApiClient();
      const response = await apiClient(api, `/stand-up/news?news_id=${newsId}`, 'DELETE');

      if (response?.status === 200 || response?.success) {
        // Only remove from local state after successful API response
        setLocalNewsList(prev => prev.filter(news => news.id !== newsId));
        
        dispatch(setToastNotification({
          header: 'Success',
          subtext: 'News deleted successfully',
          variant: 'success',
          position: 'top',
          duration: 3000,
        }));
      } else {
        throw new Error('Failed to delete news');
      }
    } catch (error: any) {
      console.error('Error deleting news:', error);
      
      // Use centralized error handler to show overlay/modal for API failures
      handleStandUpErrorWithFriendlyMessage(
        STANDUP_API_NAMES.DELETE_NEWS,
        error,
        true, // displayModal = true → show overlay
        { newsId }
      );
    } finally {
      setDeletingNewsId(null);
    }
  };

  const handleSave = async () => {
    // Clear previous inline error
    setErrorMessage(null);

    if (localNewsList.length === 0) {
      const msg = 'Please add at least one news item before saving';
      setErrorMessage(msg);
      // ensure the overlay scrolls to top so the error is visible
      scrollRef.current?.scrollTo({ y: 0, animated: true });
      return;
    }

    setIsSaving(true);
    try {
      // Use the hook to save each news item to the backend
      const savedNews: NewsItem[] = [];
      for (const news of localNewsList) {
        const result = await addNews(news);
        if (!result) {
          // Hook already dispatched error toast, throw to show inline error
          throw new Error('Failed to save a news item');
        }
        savedNews.push({
          ...result,
          is_favourite: result.is_favourite ?? false,
        });
      }

      // Call onSave with the saved news items (includes IDs from API response)
      onSave(savedNews);

      // Show success toast
      dispatch(setToastNotification({
        header: "Success",
        subtext: "News saved successfully",
        variant: "success",
        position: "top",
        duration: 3000,
      }));

      // Clear local state
      setErrorMessage(null);
      setNewsTitle('');
      setNewsDescription('');
      setLocalNewsList([]);
      onDismiss();
    } catch (error: any) {
      console.error('Error saving news:', error);

      let errorMessage = 'Failed to save news. Please try again.';
      if (error?.message) {
        errorMessage = error.message;
      }
      
      // Use centralized error handler to show overlay/modal for API failures
      handleStandUpErrorWithFriendlyMessage(
        STANDUP_API_NAMES.ADD_NEWS,
        new Error(errorMessage),
        true, // displayModal = true → show overlay
        { locationId, locationName, itemCount: localNewsList.length }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddToNews = () => {
    // Clear previous inline error
    setErrorMessage(null);

    // Validate and set error states
    const isTitleEmpty = !newsTitle.trim();
    const isDescriptionEmpty = !newsDescription.trim();
    
    setTitleError(isTitleEmpty);
    setDescriptionError(isDescriptionEmpty);

    if (isTitleEmpty || isDescriptionEmpty) {
      return;
    }

    // Capture local timestamp and timezone for backend conversion to UTC
    const clientTimestamp = getLocalTimestamp();
    const clientTimezone = getTimezoneOffsetString();

    // Format current UTC date without timezone conversion
    const formatCurrentDate = (): string => {
      const now = new Date();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const day = now.getUTCDate();
      const month = now.getUTCMonth();
      const year = now.getUTCFullYear();
      return `${day} ${monthNames[month]} ${year}`;
    };

    const newNews: NewsItem = {
      id: `news-${Date.now()}`,
      title: newsTitle.trim(),
      is_favourite: false,
      description: newsDescription.trim(),
      date: formatCurrentDate(),
      createdBy: userEmail, // Store email internally
      clientTimestamp: clientTimestamp, // Send local timestamp to backend
      clientTimezone: clientTimezone, // Send timezone for UTC conversion
    };
    setLocalNewsList(prev => [newNews, ...prev]);
    setNewsTitle('');
    setNewsDescription('');
    setTitleError(false);
    setDescriptionError(false);
  };

  console.log('Rendering NewsFeedOverlay with news list:', localNewsList);

  return (
    <LHOverlay
      visible={visible}
      onDismiss={handleCancel}
      title="News feed"
      size="large"
      close={true}
      dismissOnClickOutside={false}
      hideFooter={true}
      backgroundColor="#FFFFFF"
      headerPaddingHorizontal={40}
      headerPaddingTop={24}
      hideHeaderDivider={true}
      bodyPadding={{ horizontal: 40, vertical: { top: 24, bottom: 24 } }}
      containerStyle={{ maxWidth: 1200 }}
    >
      {/* Wrap content in a ScrollView so we can scroll to top when showing inline errors */}
      <ScrollView ref={scrollRef} style={{ flex: 1 }} keyboardShouldPersistTaps="handled">
        <View style={styles.newsContainer}>
          {/* Inline error banner shown at top of overlay so user doesn't miss validation/server errors */}
          {errorMessage && (
            <View style={styles.errorBanner}>
              <Text style={styles.errorBannerText}>{errorMessage}</Text>
            </View>
          )}
          {/* Main Content */}
          <View style={styles.newsContent}>
            {/* Left Side - Input Form */}
            <View style={styles.newsLeftCard}>
              <View style={styles.newsInputContainer}>
                <View>
                  <TextInput
                    style={[styles.newsTitleInput, titleError && styles.inputError]}
                    placeholder="Title"
                    placeholderTextColor="#9E9E9E"
                    value={newsTitle}
                    onChangeText={(text) => {
                      setNewsTitle(text);
                      if (titleError) setTitleError(false);
                    }}
                    maxLength={50}
                  />
                  {titleError && (
                    <View style={styles.errorMessageContainer}>
                      <InfoIcon />
                      <Text style={styles.errorMessageText}>This field is required to continue</Text>
                    </View>
                  )}
                </View>
                <TextInput
                  style={[styles.newsDescriptionInput, descriptionError && styles.inputError]}
                  placeholder={descriptionError ? "Please complete this field to continue" : "Description"}
                  placeholderTextColor={descriptionError ? "#C12C01" : "#9E9E9E"}
                  value={newsDescription}
                  onChangeText={(text) => {
                    setNewsDescription(text);
                    if (descriptionError) setDescriptionError(false);
                  }}
                  multiline
                  textAlignVertical="top"
                />
                <TouchableOpacity
                  style={styles.addNewsButton}
                  onPress={handleAddToNews}
                  activeOpacity={0.7}
                >
                  <PlusIcon />
                  <Text style={styles.addNewsButtonText}>Add to News</Text>
                </TouchableOpacity>
              </View>
            </View>

            {/* Right Side - News Feed */}
            <View style={styles.newsRightCard}>
              {localNewsList.length === 0 ? (
                // Empty State
                <View style={styles.newsEmptyState}>
                  <Svg width="210" height="160" viewBox="0 0 210 160" fill="none">
                    <Path d="M70 120C70 120 60 115 50 120C40 125 35 130 35 130" stroke="#D0D0D0" strokeWidth="2" strokeLinecap="round" />
                    <Path d="M140 120C140 120 150 115 160 120C170 125 175 130 175 130" stroke="#D0D0D0" strokeWidth="2" strokeLinecap="round" />
                    <Rect x="40" y="50" width="130" height="80" rx="8" fill="#F5F5F5" stroke="#D0D0D0" strokeWidth="2" />
                    <Rect x="55" y="65" width="100" height="8" rx="4" fill="#E0E0E0" />
                    <Rect x="55" y="80" width="80" height="6" rx="3" fill="#E8E8E8" />
                    <Rect x="55" y="92" width="100" height="6" rx="3" fill="#E8E8E8" />
                    <Rect x="55" y="104" width="70" height="6" rx="3" fill="#E8E8E8" />
                    <Circle cx="80" cy="30" r="8" fill="#E0E0E0" />
                    <Circle cx="100" cy="25" r="4" fill="#E8E8E8" />
                    <Circle cx="120" cy="32" r="6" fill="#E8E8E8" />
                    <Path d="M95 45L100 40L105 45" stroke="#D0D0D0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <Path d="M30 100L35 95L40 100" stroke="#D0D0D0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <Path d="M170 100L175 95L180 100" stroke="#D0D0D0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <Rect x="15" y="135" width="180" height="4" rx="2" fill="#E8E8E8" />
                    <Path d="M85 135C85 135 85 145 95 145C105 145 105 135 105 135" stroke="#E0E0E0" strokeWidth="2" />
                    <Path d="M105 135C105 135 105 145 115 145C125 145 125 135 125 135" stroke="#E0E0E0" strokeWidth="2" />
                    <Circle cx="45" cy="75" r="3" fill="#D0D0D0" />
                    <Circle cx="165" cy="85" r="2" fill="#D8D8D8" />
                    <Circle cx="25" cy="60" r="2.5" fill="#D8D8D8" />
                    <Path d="M90 15L105 25L95 30Z" fill="#E0E0E0" />
                  </Svg>
                  <Text style={styles.newsEmptyStateText}>No news added</Text>
                </View>
              ) : (
                // News Feed
                <ScrollView
                  style={styles.newsFeedScroll}
                  showsVerticalScrollIndicator={true}
                >
                  {localNewsList.map((news) => (
                    <View key={news.id} style={styles.newsItem}>
                      {showDeleteButton && (
                        <TouchableOpacity
                          style={styles.newsItemIcon}
                          onPress={() => handleDeleteNews(news.id)}
                          activeOpacity={0.7}
                          disabled={deletingNewsId === news.id}
                        >
                          {deletingNewsId === news.id ? (
                            <ActivityIndicator size="small" color="#034895" />
                          ) : (
                            <DeleteIcon />
                          )}
                        </TouchableOpacity>
                      )}
                      <View style={styles.newsItemContent}>
                        <View style={styles.newsItemHeader}>
                          <Text style={styles.newsItemTitle}>{news.title}</Text>
                          <Text style={styles.newsItemDate}>{news.date}</Text>
                        </View>
                        <View style={styles.newsItemDescriptionRow}>
                          <Text style={styles.newsItemDescription}>
                            {news.description}
                          </Text>
                          <Text style={styles.newsItemCreator}>Created By {formatUserName(news.createdBy)}</Text>
                        </View>
                      </View>
                    </View>
                  ))}
                </ScrollView>
              )}
            </View>
          </View>

          {/* Footer with Cancel and Save buttons */}
          <View style={styles.footerContainer}>
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={handleCancel}
              activeOpacity={0.7}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.saveButton, isSaving && styles.saveButtonDisabled]}
              onPress={handleSave}
              activeOpacity={0.7}
              disabled={isSaving}
            >
              {isSaving ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>Save</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </LHOverlay>
  );
};

const styles = StyleSheet.create({
  newsContainer: {
    flex: 1,
  },
  newsContent: {
    flexDirection: 'row',
    gap: 24,
    alignItems: 'flex-start',
    marginBottom: 24,
  },
  newsLeftCard: {
    flex: 1,
  },
  newsInputContainer: {
    gap: 16,
  },
  newsTitleInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#C0C0C0',
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#1C1C1C',
    height: 44,
  },
  inputError: {
    borderColor: '#AB2B09',
  },
  errorMessageContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 6,
  },
  errorMessageText: {
    fontSize: 12,
    color: '#C12C01',
    fontFamily: FontFamily.regular,
  },
  newsDescriptionInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#C0C0C0',
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#1C1C1C',
    height: 120,
    textAlignVertical: 'top',
  },
  addNewsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#005CBC',
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignSelf: 'flex-end',
  },
  addNewsButtonDisabled: {
    opacity: 0.5,
  },
  addNewsButtonText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#005CBC',
    fontFamily: FontFamily.bold,
  },
  newsRightCard: {
    flex: 1.5,
    backgroundColor: '#F5F9FF',
    borderRadius: 8,
    padding: 28,
    height: 400,
    maxHeight: 400,
    justifyContent: 'center',
    alignItems: 'stretch',
  },
  newsEmptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  newsEmptyStateText: {
    fontSize: 16,
    fontWeight: '400',
    color: '#616161',
    fontFamily: FontFamily.regular,
    marginTop: 16,
  },
  newsFeedScroll: {
    flex: 1,
  },
  newsItem: {
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    gap: 12,
    // borderWidth: 1,
    borderColor: '#E5E5E5',
  },
  newsItemIcon: {
    width: 32,
    height: 32,
    borderRadius: 4,
    backgroundColor: '#E3F2FD',
    justifyContent: 'center',
    alignItems: 'center',
  },
  newsItemContent: {
    flex: 1,
  },
  newsItemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  newsItemTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1C',
    fontFamily: FontFamily.bold,
    flex: 1,
  },
  newsItemDate: {
    fontSize: 12,
    fontWeight: '400',
    color: '#616161',
    fontFamily: FontFamily.regular,
    marginHorizontal: 8,
  },
  newsItemDescriptionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
  },
  newsItemDescription: {
    fontSize: 14,
    fontWeight: '400',
    color: '#4B4B4B',
    fontFamily: FontFamily.regular,
    lineHeight: 20,
    flex: 1,
  },
  newsItemCreator: {
    fontSize: 12,
    fontWeight: '400',
    color: '#616161',
    fontFamily: FontFamily.regular,
  },
  footerContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    paddingTop: 24,
  },
  cancelButton: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    backgroundColor: '#FFFFFF',
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#034895',
    fontFamily: FontFamily.bold,
  },
  saveButton: {
    paddingHorizontal: 32,
    paddingVertical: 10,
    borderRadius: 9999,
    backgroundColor: '#005CBC',
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
    fontFamily: FontFamily.bold,
  },
  errorBanner: {
    backgroundColor: '#FEF2F2',
    borderLeftWidth: 4,
    borderLeftColor: '#F87171',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 4,
    marginBottom: 16,
  },
  errorBannerText: {
    color: '#B91C1C',
    fontSize: 14,
    fontWeight: '500',
  },
});

export default NewsFeedOverlay;
